import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { EmailOutlined, Close } from "@mui/icons-material";
import React, { useState } from "react";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "./ModalLoadingOverlay";

export const EditEmailPopup = ({ open, onClose, onSave, candidateId }) => {
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSaveEmail = async () => {
    if (!newEmail || !confirmEmail) {
      setError(true);
      setErrorMessage("Please fill both fields!");
      return;
    }

    if (newEmail !== confirmEmail) {
      setError(true);
      setErrorMessage("Emails do not match");
      return;
    }

    setLoading(true);
    try {
      await onSave(candidateId, newEmail, confirmEmail);
      setTimeout(() => {
        setNewEmail("");
        setConfirmEmail("");
        setError(false);
        setErrorMessage("");
        setLoading(false);
        handleClose();
      }, 500);
    } catch (err) {
      setError(true);
      const backendError =
        err.response?.data?.message || "Failed to update email";
      setErrorMessage(backendError);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewEmail("");
    setConfirmEmail("");
    setError(false);
    setErrorMessage("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      keepMounted={false}
      PaperProps={{
        sx: modalLoadingContainerSx(loading, { borderRadius: "12px" }),
      }}
    >
      {/* Title Row */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          fontWeight: 700,
          fontSize: "18px",
        }}
      >
        Edit Email
        <IconButton onClick={handleClose} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Icon */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#e8eaf6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid #c5cae9",
            }}
          >
            <EmailOutlined sx={{ fontSize: 38, color: "#5c6bc0" }} />
          </Box>
        </Box>

        {/* Confirmation Text */}
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 600,
            fontSize: "15px",
            mb: 2,
          }}
        >
          Are you sure, you want to update the{" "}
          <Box component="span" sx={{ color: "#1976d2" }}>
            Email Address ?
          </Box>
        </Typography>

        {/* Divider */}
        <Box sx={{ borderTop: "1px solid #e0e0e0", mb: 2 }} />

        {/* Input Fields */}
        <TextField
          autoFocus
          margin="dense"
          label="New Email"
          type="email"
          fullWidth
          variant="outlined"
          value={newEmail}
          onChange={(e) => {
            setNewEmail(e.target.value);
            setError(false);
            setErrorMessage("");
          }}
          onPaste={(e) => e.preventDefault()}
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          sx={{ mb: 2 }}
          disabled={loading}
        />
        <TextField
          margin="dense"
          label="Confirm Email"
          type="password"
          fullWidth
          variant="outlined"
          value={confirmEmail}
          onChange={(e) => {
            setConfirmEmail(e.target.value);
            setError(false);
            setErrorMessage("");
          }}
          onPaste={(e) => e.preventDefault()}
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          error={error}
          helperText={errorMessage}
          disabled={loading}
        />
      </DialogContent>

      {/* Divider before buttons */}
      <Box sx={{ borderTop: "1px solid #e0e0e0" }} />

      {/* Action Buttons - right aligned */}
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          className="cancel-btn"
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            color: "#555",
            borderColor: "#ccc",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveEmail}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
      <ModalLoadingOverlay open={loading} />
    </Dialog>
  );
};
